import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  useGetDeviceInfo,
  getGetDeviceInfoQueryKey,
  type DeviceInfo,
} from "@workspace/api-client-react";
import {
  clearCredential,
  persistCredential,
  readStoredCredential,
  setActiveScreenCredential,
} from "@/player/lib/screen-credential";
import { clearOfflineStore, loadLastSync } from "@/player/lib/offline-store";
import { clearOperatorExited } from "@/lib/operator-exit";
import { setDevLogDevice } from "@/lib/devlog-boot";

function initialCredential(): string | null {
  return readStoredCredential();
}

interface DeviceContextValue {
  credential: string | null;
  device: DeviceInfo | null;
  /** Device came from the offline cache because the server is unreachable. */
  deviceFromCache: boolean;
  isActive: boolean;
  isLoading: boolean;
  error: Error | null;
  adoptCredential: (credential: string) => void;
  release: () => void;
  refetch: () => void;
}

const DeviceContext = createContext<DeviceContextValue | null>(null);

export function DeviceProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [credential, setCredential] = useState<string | null>(() =>
    initialCredential(),
  );

  const {
    data: device,
    isLoading,
    error,
    refetch,
  } = useGetDeviceInfo({
    query: {
      enabled: Boolean(credential),
      retry: false,
      staleTime: 30_000,
      queryKey: getGetDeviceInfoQueryKey(),
    },
  });

  const adoptCredential = useCallback((token: string) => {
    // Deliberate (re-)entry into the player surface re-arms the kiosk guard.
    clearOperatorExited();
    persistCredential(token);
    setActiveScreenCredential(token);
    setCredential(token);
  }, []);

  const release = useCallback(() => {
    clearCredential();
    setActiveScreenCredential(null);
    setCredential(null);
    queryClient.clear();
    void clearOfflineStore();
  }, [queryClient]);

  // Resume a credential written before DeviceProvider mounted (e.g. page reload).
  useEffect(() => {
    const stored = readStoredCredential();
    if (stored) {
      setCredential((current) => current ?? stored);
    }
  }, []);

  // A 401 on device-info means the credential was revoked or expired
  // server-side — drop it so every future load doesn't retry a dead token.
  useEffect(() => {
    if (credential && error instanceof ApiError && error.status === 401) {
      release();
    }
  }, [credential, error, release]);

  // Offline-first device resolution (CB-160). A NETWORK failure on /device/me
  // is not a sign-out: a provisioned box must boot and play from its cached
  // window. Fall back to the DeviceInfo inside the cached /device/sync payload;
  // only a confirmed 401 (handled above) ever drops the credential. Cleared as
  // soon as the live query succeeds again.
  const [cachedDevice, setCachedDevice] = useState<DeviceInfo | null>(null);
  useEffect(() => {
    if (!credential || device) {
      setCachedDevice(null);
      return;
    }
    if (!error) return;
    if (error instanceof ApiError && error.status === 401) return;
    let alive = true;
    void loadLastSync().then((sync) => {
      if (alive && sync?.device) setCachedDevice(sync.device as DeviceInfo);
    });
    return () => {
      alive = false;
    };
  }, [credential, device, error]);

  // Recovery loop: with retry:false the query never re-runs on its own after a
  // network failure, so an offline box would previously stay "signed out"
  // forever even after the network came back. Poll gently while errored.
  useEffect(() => {
    if (!credential || device) return;
    if (!error || (error instanceof ApiError && error.status === 401)) return;
    const timer = window.setInterval(() => {
      void refetch();
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [credential, device, error, refetch]);

  useEffect(() => {
    const onRevoked = () => release();
    const onDeviceUpdated = () => {
      void refetch();
    };
    window.addEventListener("omnimedia:credential-revoked", onRevoked);
    window.addEventListener("omnimedia:device-updated", onDeviceUpdated);
    return () => {
      window.removeEventListener("omnimedia:credential-revoked", onRevoked);
      window.removeEventListener("omnimedia:device-updated", onDeviceUpdated);
    };
  }, [release, refetch]);

  const effectiveDevice = device ?? cachedDevice;

  // DEVLOGS-TEMP: stamp device identity onto every debug-log record. Registered
  // as a GETTER rather than a captured value because identity is not known at
  // boot — a player has no deviceId until it has claimed a credential and
  // resolved /device/me. Inert when the pipeline is unconfigured.
  useEffect(() => {
    setDevLogDevice({
      deviceId: effectiveDevice?.id,
      workspaceId: effectiveDevice?.workspaceId,
      locationId: effectiveDevice?.locationId ?? undefined,
    });
  }, [effectiveDevice?.id, effectiveDevice?.workspaceId, effectiveDevice?.locationId]);

  const value = useMemo<DeviceContextValue>(
    () => ({
      credential,
      device: effectiveDevice ?? null,
      deviceFromCache: !device && Boolean(cachedDevice),
      isActive: Boolean(credential && effectiveDevice),
      isLoading: Boolean(credential) && isLoading,
      error: (error as Error) ?? null,
      adoptCredential,
      release,
      refetch: () => {
        void refetch();
      },
    }),
    [
      credential,
      device,
      cachedDevice,
      effectiveDevice,
      isLoading,
      error,
      adoptCredential,
      release,
      refetch,
    ],
  );

  return (
    <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>
  );
}

export function useDevice(): DeviceContextValue {
  const ctx = useContext(DeviceContext);
  if (!ctx) {
    throw new Error("useDevice must be used within a DeviceProvider");
  }
  return ctx;
}
